import { useTranslation } from 'react-i18next';
import { GanttTask, ComputedTask } from '../types/types';
import { ROW_H, HEADER_H, ActionStatus } from '../constants';
import { fmtGrid } from '../dateUtils';
import StatusTag from './StatusTag';

interface VisibleRow {
  task: GanttTask;
  level: number;
}

interface GanttLeftPanelProps {
  visibleRows: VisibleRow[];
  computed: Map<number | string, ComputedTask>;
  selectedId: number | string | null;
  isSummary: (task: GanttTask) => boolean;
  resolveStatus: (task: GanttTask, c: ComputedTask) => ActionStatus;
  gridScrollRef: React.RefObject<HTMLDivElement>;
  onGridScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onSelect: (id: number | string) => void;
  onToggleExpand: (task: GanttTask) => void;
  onRowDragStart: (id: number | string) => void;
  onRowDrop: (task: GanttTask) => void;
}

export default function GanttLeftPanel({
  visibleRows,
  computed,
  selectedId,
  isSummary,
  resolveStatus,
  gridScrollRef,
  onGridScroll,
  onSelect,
  onToggleExpand,
  onRowDragStart,
  onRowDrop,
}: GanttLeftPanelProps) {
  const { t } = useTranslation('improvementPlan');
  return (
    <div style={{ width: 360, minWidth: 360, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: HEADER_H, display: 'flex', alignItems: 'center', borderBottom: '1px solid #e0e0e0', fontWeight: 600, color: '#5f6368', flexShrink: 0 }}>
        <div style={{ flex: 1, padding: '0 12px' }}>{t('ganttView.taskName')}</div>
        <div style={{ width: 120, padding: '0 12px' }}>{t('ganttView.status')}</div>
      </div>
      <div ref={gridScrollRef} onScroll={onGridScroll} style={{ overflow: 'auto', flex: 1 }}>
        {visibleRows.map(({ task, level }) => {
          const c = computed.get(task.id)!;
          const summary = isSummary(task);
          const sel = task.id === selectedId;
          return (
            <div
              key={task.id}
              draggable
              onDragStart={() => onRowDragStart(task.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onRowDrop(task)}
              onClick={() => onSelect(task.id)}
              style={{ display: 'flex', alignItems: 'center', height: ROW_H, cursor: 'pointer', background: sel ? '#e8f0fe' : 'transparent', borderBottom: '1px solid #f1f3f4', boxSizing: 'border-box' }}
            >
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', paddingLeft: 12 + level * 16, paddingRight: 8 }}>
                {summary ? (
                  <span onClick={(e) => { e.stopPropagation(); onToggleExpand(task); }} style={{ display: 'inline-block', width: 16, cursor: 'pointer', color: '#5f6368', transition: 'transform .15s', transform: task.expanded ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}>▾</span>
                ) : (
                  <span style={{ width: 16, display: 'inline-block', flexShrink: 0 }} />
                )}
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <span style={{ fontWeight: summary ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.name}</span>
                  <span style={{ fontSize: 11, color: '#5f6368', whiteSpace: 'nowrap' }}>
                    {fmtGrid(c.start)} – {fmtGrid(c.end)}
                  </span>
                </div>
              </div>
              <div style={{ width: 120, padding: '0 12px', display: 'flex', alignItems: 'center' }}>
                <StatusTag status={resolveStatus(task, c)} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
